
import { useQuery } from '@tanstack/react-query';
import { useAllResources, useResourceCategories } from './useResources';

export interface ResourceStats {
  totalResources: number;
  aiTools: number;
  templates: number;
  videos: number;
  guides: number;
  articles: number;
  categoryStats: Array<{
    name: string;
    icon: string;
    count: number;
  }>;
}

export const useResourceStats = () => {
  const { data: allResources, isLoading: resourcesLoading } = useAllResources();
  const { data: categories, isLoading: categoriesLoading } = useResourceCategories();

  return useQuery({
    queryKey: ['resource-stats', allResources, categories],
    queryFn: (): ResourceStats => {
      if (!allResources || !categories) {
        return {
          totalResources: 0,
          aiTools: 0,
          templates: 0,
          videos: 0,
          guides: 0,
          articles: 0,
          categoryStats: []
        };
      }

      // Count resources by type
      const typeStats = allResources.reduce((acc, resource) => {
        acc[resource.type] = (acc[resource.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Count resources by category
      const categoryStats = categories.map(category => {
        const count = allResources.filter(resource => resource.category === category.slug).length;
        return {
          name: category.name,
          icon: getTypeIcon(category.name),
          count
        };
      }).filter(stat => stat.count > 0);

      return {
        totalResources: allResources.length,
        aiTools: typeStats.tool || 0,
        templates: typeStats.template || 0,
        videos: typeStats.video || 0,
        guides: typeStats.guide || 0,
        articles: typeStats.article || 0,
        categoryStats
      };
    },
    enabled: !resourcesLoading && !categoriesLoading && !!allResources && !!categories,
  });
};

const getTypeIcon = (categoryName: string): string => {
  const name = categoryName.toLowerCase();
  if (name.includes('prompt') || name.includes('engineering')) return '📚';
  if (name.includes('tool') || name.includes('ai')) return '🔧';
  if (name.includes('template')) return '📋';
  if (name.includes('video') || name.includes('tutorial')) return '🎥';
  if (name.includes('guide') || name.includes('article')) return '📄';
  return '📄';
};
